import { Avatar, AvatarProps, Box, Skeleton, SkeletonProps, Theme, useMediaQuery } from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '..';
import { AvatarSize, IconSize } from '../../types';
import { CustomAvatar } from '../../types/Avatar';

interface BaseAvatarProps {
  avatar: CustomAvatar;
  dataTestIdPrefix: string;
  altText: string;
  isLoading?: boolean;
}

/**
 * Custom Avatar component that can be used to render an icon or an image, with colour masking
 * @param avatar - object defining the avatar props
 * @param dataTestIdPrefix - prefix for the data-test-id
 * @param altText - alt text for the avatar
 * @param isLoading - boolean to indicate if the avatar is loading
 */
const BaseAvatar: React.FC<BaseAvatarProps> = ({ isLoading, dataTestIdPrefix, avatar, altText }) => {
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const avatarSize = avatar.size?.container || (isSmallDevice ? AvatarSize.SM : AvatarSize.MD);
  const iconSize = avatar.size?.icon || (isSmallDevice ? IconSize.MD : IconSize.LG);

  const avatarLoadingVariantMap: Record<AvatarProps['variant'], SkeletonProps['variant']> = {
    circular: 'circular',
    rounded: 'rectangular',
    square: 'rectangular',
  };

  return (
    <>
      {isLoading ? (
        <Skeleton
          variant={avatarLoadingVariantMap[avatar.variant || 'rounded']}
          animation="wave"
          width={avatarSize}
          height={avatarSize}
          sx={{ mr: 4 }}
          data-testid="loading-skeleton"
        />
      ) : null}
      {avatar && !isLoading ? (
        <Box
          mr={4}
          sx={{
            height: `${avatarSize}px`,
            width: `${avatarSize}px`,
            '& .MuiAvatar-root': { height: '100%', width: '100%' },
            flex: `${avatarSize}px 0 0`,
          }}
        >
          <Avatar
            variant={avatar.variant}
            sx={{ ...(avatar.bgcolor && { bgcolor: avatar.bgcolor }) }}
            src={avatar.mode === 'image' ? avatar.imagePath : undefined}
            alt={t('ns.common:card:image_alt', { ref: altText })}
            data-testid={`${dataTestIdPrefix}-card-header-avatar`}
          >
            {avatar.mode === 'icon' ? (
              <Icon
                fullPath={avatar.imagePath}
                size={iconSize}
                fallback={<>{avatar.fallback}</>}
                simpleColorMask={{ size: avatarSize, color: avatar.colorMask }}
              />
            ) : (
              avatar.fallback
            )}
          </Avatar>
        </Box>
      ) : null}
    </>
  );
};

export default BaseAvatar;
